import { Classes, Dialog, Intent, Colors, ButtonGroup, Checkbox } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, useGridState, ButtonContainer, useSearchApi } from "nsitools-react";

// import { useSearchApi } from "../../../../../../hooks/useLocalSearchApi";

import * as React from "react";
import styled from "styled-components";

import { DogCompetitionResultGridDto, DogCompetitionResultSearch, DogResultsApi } from "../../../../../../api";
import { AddButton, DeleteButton, CustomDataTable, ExportButton, EditButton } from "../../../../../../components";
import { useApiService, useTl } from "../../../../../../hooks";
import { ETLCodes } from "../../../../../../locales";
import { DogResultPanel } from "./DogResultPanel";
import { useDialog, useGlobalData, useEventsContext } from "../../../../../../contexts";
import { Row } from "react-flexbox-grid";

export interface IDogResultsListProps {
  dogId: number;
  editMode: boolean;
}

const Container = styled.div`
  flex: 1;
  height: 100%;
  & > .${Classes.PANEL_STACK} {
    height: 100%;
  }
`;

export const DogResultsList: React.FunctionComponent<IDogResultsListProps> = ({ dogId, editMode }) => {
  const api = useApiService(DogResultsApi);
  const { showDialog } = useDialog();
  const { t } = useTl();
  const [selectedResultId, setSelectedResultId] = React.useState<number>(null);
  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: false,
    availablePageSizes: [5, 10, 15],
    pageSize: 10,
    sortKeys: { dateFrom: "DESC" },
  });
  const { currentLanguage } = useGlobalData();
  const searchFunction = React.useCallback(
    (sObj?: DogCompetitionResultSearch) => {
      setsobjState(DogCompetitionResultSearch.fromJS({ ...sObj, dogId: dogId }));
      return api.baseSearch(DogCompetitionResultSearch.fromJS({ ...sObj, dogId: dogId }));
    },
    [api, dogId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });
  const deleteItemFunction = React.useCallback(
    (breedColorId) => {
      showDialog({
        message: t(ETLCodes.DeleteConfirmationMessage),
        title: t(ETLCodes.DeleteDialogTitle),
        onConfirmed: async () => {
          api.deleteResult(breedColorId).then(() => search());
        },
      });
    },
    [api, search, showDialog, t]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    search();
  }, [search]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("ReloadTitlesTable", onRequestRefreshEvent);
    return () => unsubscribeEvent("ReloadTitlesTable", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: DogCompetitionResultGridDto) => (
          <ButtonContainer>
            <EditButton minimal={true} onClick={() => setSelectedResultId(row.competitionResultId)} />
            <DeleteButton minimal={true} onDelete={() => deleteItemFunction(row.competitionResultId)} />
          </ButtonContainer>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "isLinkedToTitle",
        autoFitContent: true,
        render: (row: DogCompetitionResultGridDto) => row.isLinkedToTitle && <i className="fas fa-trophy" />,
      },
      {
        header: () => t(ETLCodes.Competition),
        fieldName: "competition",
        computed: true,
        disableSorting: true,
      },
      {
        header: () => t(ETLCodes.Expo),
        fieldName: "expo",
      },
      {
        header: () => t(ETLCodes.Judge),
        fieldName: "judge",
      },
      {
        header: () => t(ETLCodes.Type),
        fieldName: "showClass",
        render: (row: DogCompetitionResultGridDto) => <div>{row?.showClass === "\u0000" ? "" : row.showClass}</div>,
      },
      {
        header: () => t(ETLCodes.Qualification),
        fieldName: "qualification",
      },
      {
        header: () => t(ETLCodes.Result),
        fieldName: "result",
      },
      {
        header: () => t(ETLCodes.Cac),
        fieldName: "cac",
        alignment: "center",
        render: (row: DogCompetitionResultGridDto) => <Checkbox checked={row.cac} disabled />,
      },
      {
        header: () => t(ETLCodes.CacReserve),
        fieldName: "cacReserve",
        alignment: "center",
        render: (row: DogCompetitionResultGridDto) => <Checkbox checked={row.cacReserve} disabled />,
      },
      {
        header: () => t(ETLCodes.Cacib),
        fieldName: "cacib",
        alignment: "center",
        render: (row: DogCompetitionResultGridDto) => <Checkbox checked={row.cacib} disabled />,
      },
      {
        header: () => t(ETLCodes.CacibReserve),
        fieldName: "cacibReserve",
        alignment: "center",
        render: (row: DogCompetitionResultGridDto) => <Checkbox checked={row.cacibReserve} disabled />,
      },
    ],
    [deleteItemFunction, editMode, t]
  );
  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.Competitions)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode && (
              <AddButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedResultId(0);
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
      <Dialog
        title={t(ETLCodes.Competition)}
        isOpen={selectedResultId === 0 || !!selectedResultId}
        onClose={() => setSelectedResultId(null)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1500, height: 700, background: Colors.WHITE }}
        className={Classes.DIALOG}
      >
        <Container className={Classes.DIALOG_BODY}>
          <DogResultPanel
            dogId={dogId}
            selectedResultId={selectedResultId}
            onSaved={() => {
              setSelectedResultId(null);
              search();
            }}
          />
        </Container>
      </Dialog>
    </>
  );
};
